import { boundedSessionRevoke, validateSessionPair } from '../../shared-types/src/session-client.js';
import { ServiceClientError } from '../../service-client/src/http-client.js';
import type { AdminHttpClient } from './http.js';
import type { AdminSession } from './types.js';

const cancelled = () => new ServiceClientError(409, { error: 'session_cancelled' });
const expired = () => new ServiceClientError(401, { error: 'session_reauthentication_required' });
const unauthorized = (error: unknown) => (error as { status?: number } | undefined)?.status === 401;

/** One instance per tab. No token broadcasting or shared plaintext storage. */
export class AdminSessionLifecycle {
  generation = 0;
  contextGeneration = 0;
  private refreshing: Promise<void> | undefined;
  constructor(
    readonly read: () => AdminSession | undefined,
    private readonly write: (next: AdminSession | undefined) => void,
    private readonly onExpired: () => void,
  ) {}
  assertCurrent(generation: number, context?: number): void {
    if (generation !== this.generation || (context !== undefined && context !== this.contextGeneration)) throw cancelled();
  }
  clear(notify = true): void {
    this.generation++; this.contextGeneration++; this.refreshing = undefined;
    this.write(undefined);
    if (notify) this.onExpired();
  }
  beginLogin(): number { this.clear(false); return this.generation; }
  acceptLogin(next: AdminSession, generation: number): void {
    this.assertCurrent(generation);
    const clean = { ...next }; delete clean.tenantContext;
    this.write(clean);
  }
  updateContext(next: AdminSession | undefined): void {
    if (!next) { this.clear(); return; }
    const current = this.read();
    if (!current || next.accessToken !== current.accessToken) throw cancelled();
    // Fence only a tenant switch: a silent same-tenant renewal must not 409 requests still in flight.
    if (next.tenantContext?.tenantId !== current.tenantContext?.tenantId) this.contextGeneration++;
    this.write(next);
  }
  async renew(http: AdminHttpClient): Promise<void> {
    if (this.refreshing) return this.refreshing;
    const current = this.read(); const generation = this.generation;
    if (!current?.refreshToken) throw expired();
    const pending = (async () => {
      let successor: string | undefined;
      try {
        if (current.sessionProtocol === 'rotating_v1') {
          const pair = await http.refreshSession(current.refreshToken); // No automatic POST retry.
          successor = pair.refreshToken;
          this.assertCurrent(generation);
          validateSessionPair(pair, 'rotating_v1');
          if (pair.refreshExpiresAt !== current.refreshExpiresAt) throw expired();
          const latest = this.read();
          if (!latest || latest.refreshToken !== current.refreshToken) throw cancelled();
          this.write({ ...latest, ...pair }); // Single record written before publishing memory.
        } else {
          const pair = await http.refresh(current.refreshToken);
          this.assertCurrent(generation);
          const latest = this.read(); if (!latest) throw cancelled();
          this.write({ ...latest, ...pair });
        }
      } catch {
        if (generation === this.generation) this.clear();
        if (successor) void boundedSessionRevoke(() => http.revokeSession({ refreshToken: successor! }));
        throw expired();
      }
    })();
    this.refreshing = pending;
    try { await pending; } finally { if (this.refreshing === pending) this.refreshing = undefined; }
  }
  async logout(http: AdminHttpClient): Promise<'confirmed' | 'unconfirmed'> {
    const current = this.read(); this.clear();
    return current?.sessionProtocol === 'rotating_v1'
      ? boundedSessionRevoke(() => http.revokeSession({ refreshToken: current.refreshToken }))
      : 'unconfirmed';
  }
}

/** Fence every response, including failures, before it can retry or expire a newer session. */
export function withSessionRequests(http: AdminHttpClient, lifecycle: AdminSessionLifecycle, tenant = false): AdminHttpClient {
  return new Proxy(http, {
    get(target, prop, receiver) {
      const value = Reflect.get(target, prop, receiver);
      if (typeof value !== 'function') return value;
      if (['sessionCapabilities', 'refreshSession', 'revokeSession', 'refresh', 'loginWithPassword', 'platformLoginWithPassword', 'partnerLoginWithPassword'].includes(String(prop))) return value.bind(target);
      return async (...args: unknown[]) => {
        const generation = lifecycle.generation;
        const context = tenant ? lifecycle.contextGeneration : undefined;
        const token = lifecycle.read()?.accessToken;
        if (!token) throw expired();
        const check = () => lifecycle.assertCurrent(generation, context);
        try { const result = await value.apply(target, args); check(); return result; } catch (error) {
          check();
          // Session operations were excluded above: never retry a rotation POST.
          if (tenant || !unauthorized(error)) throw error;
          if (lifecycle.read()?.accessToken === token) await lifecycle.renew(http);
          check();
          try { const result = await value.apply(target, args); check(); return result; } catch (second) {
            check(); if (unauthorized(second)) lifecycle.clear(); throw second;
          }
        }
      };
    },
  });
}
